import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, Space } from 'antd';
import { 
  CameraOutlined, 
  CloseOutlined, 
  CheckOutlined,
  RetweetOutlined,
  LoadingOutlined,
  UploadOutlined 
} from '@ant-design/icons';
import './Camera.css';

interface CameraProps {
  visible: boolean;
  onCapture: (imageData: string) => void;
  onClose: () => void;
  title?: string;
}

const Camera: React.FC<CameraProps> = ({
  visible,
  onCapture,
  onClose,
  title = 'Take Photo'
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [showCamera, setShowCamera] = useState<boolean>(true);

  useEffect(() => {
    if (visible) {
      startCamera();
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [visible, facingMode]);

  const startCamera = async () => {
    try {
      setError(null);
      setCapturedImage(null);
      
      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      setStream(mediaStream);
      setHasPermission(true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setHasPermission(false);
      
      const error = err as Error;
      if (error.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access to take photos.');
      } else if (error.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Failed to access camera. Please try again.');
      }
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageData);

    // Stop the video stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const confirmPhoto = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      handleClose();
    }
  };

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setCapturedImage(null);
    onClose();
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setCapturedImage(result);
      setShowCamera(false);
      
      // Stop camera stream if running
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    reader.readAsDataURL(file);
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
      centered
      className="camera-modal"
    >
      <div className="camera-container">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        
        {error && showCamera ? (
          <div className="camera-error">
            <CameraOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <p>{error}</p>
            <Space>
              <Button type="primary" onClick={startCamera}>
                Try Camera Again
              </Button>
              <Button onClick={openFileSelector} icon={<UploadOutlined />}>
                Upload Photo
              </Button>
            </Space>
          </div>
        ) : capturedImage ? (
          <div className="camera-preview">
            <img src={capturedImage} alt="Captured" className="captured-image" />
            <div className="camera-actions">
              <Space size="large">
                <Button 
                  size="large" 
                  onClick={retakePhoto}
                  icon={<RetweetOutlined />}
                >
                  Retake
                </Button>
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={confirmPhoto}
                  icon={<CheckOutlined />}
                >
                  Use Photo
                </Button>
              </Space>
            </div>
          </div>
        ) : showCamera ? (
          <>
            <div className="video-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              {hasPermission === null && (
                <div className="camera-loading">
                  <LoadingOutlined style={{ fontSize: 32 }} />
                  <p>Requesting camera permission...</p>
                </div>
              )}
            </div>
            {hasPermission && (
              <div className="camera-actions">
                <Space size="large">
                  <Button onClick={handleClose} icon={<CloseOutlined />}>
                    Cancel
                  </Button>
                  <Button onClick={openFileSelector} icon={<UploadOutlined />}>
                    Upload
                  </Button>
                  <Button onClick={toggleCamera} icon={<RetweetOutlined />}>
                    Switch Camera
                  </Button>
                  <Button 
                    type="primary" 
                    size="large" 
                    onClick={capturePhoto}
                    icon={<CameraOutlined />}
                  >
                    Capture
                  </Button>
                </Space>
              </div>
            )}
          </>
        ) : (
          /* Initial choice screen */
          <div className="camera-choice">
            <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
              <div>
                <p style={{ marginBottom: 20 }}>Choose how to add your photo:</p>
              </div>
              <Space size="large">
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={() => { setShowCamera(true); startCamera(); }}
                  icon={<CameraOutlined />}
                >
                  Take Photo
                </Button>
                <Button 
                  size="large" 
                  onClick={openFileSelector}
                  icon={<UploadOutlined />}
                >
                  Upload Photo
                </Button>
              </Space>
              <Button onClick={handleClose} type="text">
                Cancel
              </Button>
            </Space>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default Camera;